import React, { useState, useEffect, useContext } from 'react';
import {
	Box,
	Card,
	CardContent,
	TextField,
	Button,
	Typography,
	Alert,
	CircularProgress,
	InputAdornment,
	IconButton,
	Stack,
	LinearProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import https from '../../base-ui/utils/https';
import { ConfigContext } from '../../base-ui/Config/ConfigContext';

export function ResetPassword() {
	const [token, setToken] = useState<string>('');
	const [email, setEmail] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const [confirmPassword, setConfirmPassword] = useState<string>('');
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const [success, setSuccess] = useState<boolean>(false);
	const navigate = useNavigate();
	const { config }: any = useContext(ConfigContext);

	useEffect(() => {
		// Extract token and email from URL query params
		const queryParams = new URLSearchParams(window.location.search);
		const verificationToken = queryParams.get('verificationToken');
		const userEmail = queryParams.get('email');

		if (!verificationToken || !userEmail) {
			setError('Invalid or missing reset token. Please request a new password reset link.');
		} else {
			setToken(verificationToken);
			setEmail(decodeURIComponent(userEmail));
		}
	}, []);

	const getPasswordStrength = (pwd: string): number => {
		let strength = 0;
		if (pwd.length >= 8) strength += 25;
		if (/(?=.*[a-z])/.test(pwd)) strength += 25;
		if (/(?=.*[A-Z])/.test(pwd)) strength += 25;
		if (/(?=.*\d)/.test(pwd)) strength += 25;
		return strength;
	};

	const getPasswordStrengthColor = (strength: number): 'error' | 'warning' | 'success' => {
		if (strength < 50) return 'error';
		if (strength < 100) return 'warning';
		return 'success';
	};

	const getPasswordStrengthLabel = (strength: number): string => {
		if (strength < 50) return 'Weak';
		if (strength < 100) return 'Good';
		return 'Strong';
	};

	const validatePassword = (pwd: string): string | null => {
		if (pwd.length < 8) {
			return 'Password must be at least 8 characters long';
		}
		if (pwd.includes('&')) {
			return 'Password cannot contain "&" character';
		}
		if (!/(?=.*[a-z])/.test(pwd)) {
			return 'Password must contain at least one lowercase letter';
		}
		if (!/(?=.*[A-Z])/.test(pwd)) {
			return 'Password must contain at least one uppercase letter';
		}
		if (!/(?=.*\d)/.test(pwd)) {
			return 'Password must contain at least one number';
		}
		return null;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');

		// Validation
		if (!password || !confirmPassword) {
			setError('Please fill in all fields');
			return;
		}

		if (password !== confirmPassword) {
			setError('Passwords do not match');
			return;
		}

		const validationError = validatePassword(password);
		if (validationError) {
			setError(validationError);
			return;
		}

		setLoading(true);

		try {
			const response = await https.post(
				{
					email: email,
					code: token,
					password: password,
				},
				`${config.uri.base}/password/confirm-reset`,
				{
					authHeader: false,
					headers: {
						'x-app-id': process.env.REACT_APP_APPID
					}
				}
			);

			if (response.errored) {
				setError(response.error?.message || 'Failed to reset password. Please try again.');
				setLoading(false);
			} else {
				setSuccess(true);
				setLoading(false);

				// Redirect to login after 3 seconds
				setTimeout(() => {
					navigate('/login');
				}, 3000);
			}
		} catch (err: any) {
			setError(err.message || 'An unexpected error occurred. Please try again.');
			setLoading(false);
		}
	};

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleClickShowConfirmPassword = () => {
		setShowConfirmPassword(!showConfirmPassword);
	};

	const passwordStrength = getPasswordStrength(password);

	if (!token && error) {
		return (
			<Box
				sx={{
					minHeight: '100vh',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: 'background.default',
					padding: 2,
				}}
			>
				<Card
					sx={{
						maxWidth: 500,
						width: '100%',
						backdropFilter: 'blur(20px)',
						backgroundColor: 'rgba(255, 255, 255, 0.95)',
						boxShadow: 3,
					}}
				>
					<CardContent sx={{ p: 4 }}>
						<Typography variant="h5" component="h1" gutterBottom align="center" sx={{ fontWeight: 600 }}>
							Invalid Reset Link
						</Typography>
						<Alert severity="error" sx={{ mt: 2 }}>
							{error}
						</Alert>
						<Button
							fullWidth
							variant="contained"
							onClick={() => navigate('/login')}
							sx={{ mt: 3 }}
						>
							Go to Login
						</Button>
					</CardContent>
				</Card>
			</Box>
		);
	}

	if (success) {
		return (
			<Box
				sx={{
					minHeight: '100vh',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: 'background.default',
					padding: 2,
				}}
			>
				<Card
					sx={{
						maxWidth: 500,
						width: '100%',
						backdropFilter: 'blur(20px)',
						backgroundColor: 'rgba(255, 255, 255, 0.95)',
						boxShadow: 3,
					}}
				>
					<CardContent sx={{ p: 4 }}>
						<Box sx={{ textAlign: 'center', mb: 3 }}>
							<CheckCircle sx={{ fontSize: 64, color: 'primary.600', mb: 2 }} />
							<Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
								Password Reset Successful
							</Typography>
						</Box>
						<Alert severity="success" sx={{ mt: 2 }}>
							Your password has been successfully reset. You will be redirected to the login page in a few seconds.
						</Alert>
						<Button
							fullWidth
							variant="contained"
							onClick={() => navigate('/login')}
							sx={{ mt: 3 }}
						>
							Go to Login Now
						</Button>
					</CardContent>
				</Card>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				minHeight: '100vh',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				backgroundColor: 'background.default',
				padding: 2,
			}}
		>
			<Card
				sx={{
					maxWidth: 480,
					width: '100%',
					backdropFilter: 'blur(20px)',
					backgroundColor: 'rgba(255, 255, 255, 0.85)',
					border: '1px solid rgba(255, 255, 255, 0.3)',
					boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
					position: 'relative',
					zIndex: 1,
				}}
			>
				<CardContent sx={{ p: 4 }}>
					<Box sx={{ textAlign: 'center', mb: 4 }}>
						<img
							src="/deck_metrix_logo.svg"
							alt="DeckMetriX"
							style={{ height: 48, marginBottom: 24 }}
						/>
						<Typography
							variant="h1"
							sx={{
								fontSize: '1.75rem',
								fontWeight: 600,
								mb: 1,
								color: 'text.primary',
							}}
						>
							Reset Your Password
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9375rem' }}>
							Enter your new password below
						</Typography>
					</Box>

					{error && (
						<Alert severity="error" sx={{ mb: 3 }}>
							{error}
						</Alert>
					)}

					<form onSubmit={handleSubmit}>
						<Stack spacing={3}>
							<Box>
								<Typography
									component="label"
									sx={{
										display: 'block',
										fontSize: '0.875rem',
										fontWeight: 500,
										mb: 1,
										color: 'secondary.800',
									}}
								>
									New Password
								</Typography>
								<TextField
									fullWidth
									type={showPassword ? 'text' : 'password'}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									disabled={loading}
									placeholder="Enter your password"
									required
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={handleClickShowPassword}
													edge="end"
													disabled={loading}
													size="small"
												>
													{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
												</IconButton>
											</InputAdornment>
										),
									}}
									sx={{
										'& .MuiFilledInput-root': {
											backgroundColor: 'rgba(255, 255, 255, 0.9)',
										},
									}}
								/>
								{password && (
									<Box sx={{ mt: 1.5 }}>
										<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
											<Typography variant="caption" color="text.secondary">
												Password strength
											</Typography>
											<Typography
												variant="caption"
												sx={{
													fontWeight: 600,
													color: `${getPasswordStrengthColor(passwordStrength)}.main`,
												}}
											>
												{getPasswordStrengthLabel(passwordStrength)}
											</Typography>
										</Box>
										<LinearProgress
											variant="determinate"
											value={passwordStrength}
											color={getPasswordStrengthColor(passwordStrength)}
											sx={{
												height: 4,
												borderRadius: 2,
												backgroundColor: 'gray.200',
											}}
										/>
									</Box>
								)}
								<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
									Must include uppercase, lowercase, number, and be 8+ characters
								</Typography>
							</Box>

							<Box>
								<Typography
									component="label"
									sx={{
										display: 'block',
										fontSize: '0.875rem',
										fontWeight: 500,
										mb: 1,
										color: 'secondary.800',
									}}
								>
									Confirm Password
								</Typography>
								<TextField
									fullWidth
									type={showConfirmPassword ? 'text' : 'password'}
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									disabled={loading}
									placeholder="Re-enter your password"
									required
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={handleClickShowConfirmPassword}
													edge="end"
													disabled={loading}
													size="small"
												>
													{showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
												</IconButton>
											</InputAdornment>
										),
									}}
									sx={{
										'& .MuiFilledInput-root': {
											backgroundColor: 'rgba(255, 255, 255, 0.9)',
										},
									}}
								/>
							</Box>

							<Button
								type="submit"
								fullWidth
								variant="contained"
								disabled={loading}
								size="large"
								sx={{
									mt: 1,
									py: 1.5,
									fontSize: '1rem',
									fontWeight: 600,
									textTransform: 'none',
									boxShadow: 2,
									'&:hover': {
										boxShadow: 3,
									},
								}}
							>
								{loading ? (
									<CircularProgress size={24} sx={{ color: 'white' }} />
								) : (
									'Reset Password & Continue'
								)}
							</Button>
						</Stack>
					</form>
				</CardContent>
			</Card>
		</Box>
	);
}
